import type { SupabaseClient } from "@supabase/supabase-js";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import type { AnnotationSummary, Staleness, StalenessReason } from "@/lib/annotation.server";
import { runMultiSearchCore, selectCandidateJobs, type ReadyJobMeta } from "@/lib/multi-search";
import { classifyTerm, type CandidateReports, type ReportMeta } from "@/lib/search-types";

export const ensureProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId, claims } = context;
    const email = (claims as { email?: string } | null)?.email ?? null;
    const { ensureProfileRow } = await import("@/lib/jobs.server");
    return ensureProfileRow(supabase, userId, email);
  });

export const getProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

export const updateProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ notify_on_complete: z.boolean() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("profiles")
      .update({ notify_on_complete: data.notify_on_complete })
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const createJob = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        filename: z.string().trim().min(1).max(255),
        storage_path: z.string().trim().min(1).max(500),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { insertJob } = await import("@/lib/jobs.server");
    return insertJob(supabase, userId, data.filename, data.storage_path, null);
  });

/**
 * A user-chosen display name, separate from the uploaded filename — for
 * telling apart several files at a glance (e.g. one per family member)
 * without renaming the actual file. Null/empty clears it, falling back to
 * showing `filename` again (see dashboard.tsx / report.$jobId.tsx).
 */
export const renameJob = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        jobId: z.string().uuid(),
        label: z.string().trim().max(120).nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("jobs")
      .update({ label: data.label || null })
      .eq("id", data.jobId)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const startAnnotation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ jobId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { startOwnJob } = await import("@/lib/jobs.server");
    return startOwnJob(supabase, userId, data.jobId);
  });

/**
 * Self-service re-run of an already-processed job (the `ready|failed ->
 * queued` transition `protect_jobs_columns()` allows alongside the
 * `uploaded -> queued` one `startAnnotation` uses). Same rate limits as a
 * brand-new annotation — this is exactly as expensive a pipeline run, so it
 * must count against the same caps rather than being a free extra job.
 */
export const reannotateJob = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ jobId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { rerunOwnJob } = await import("@/lib/jobs.server");
    return rerunOwnJob(supabase, userId, data.jobId);
  });

/**
 * Computes, for each of the caller's own ready/failed jobs, whether rerunning
 * it today would produce different output — driven only by concrete,
 * provable-benefit signals (see `Staleness` in annotation.server.ts), never
 * by a raw pipeline_version mismatch alone. `annotation_config` is
 * admin-only under RLS, so reading "what's currently enabled" needs
 * `supabaseAdmin` — the derived boolean/reason tags returned here are the
 * only thing that reaches the client, never the raw config rows.
 */
async function computeStaleness(
  supabase: SupabaseClient<Database>,
  userId: string,
  jobs: Array<{ id: string; status: string; user_id: string }>,
): Promise<Map<string, Staleness>> {
  const own = jobs.filter(
    (job) => job.user_id === userId && (job.status === "ready" || job.status === "failed"),
  );
  const result = new Map<string, Staleness>();
  if (own.length === 0) return result;

  const jobIds = own.map((job) => job.id);
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const [
    { data: configRows },
    { data: profile },
    { data: resultRows },
    { data: haploRows },
    { data: admixRows },
  ] = await Promise.all([
    supabaseAdmin.from("annotation_config").select("key, enabled"),
    supabase
      .from("profiles")
      .select("haplogroup_opt_in, admixture_opt_in")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase.from("results").select("job_id, summary").in("job_id", jobIds),
    supabase.from("haplogroup_results").select("job_id").in("job_id", jobIds),
    supabase.from("admixture_results").select("job_id").in("job_id", jobIds),
  ]);

  const enabledSources = (configRows ?? []).filter((row) => row.enabled).map((row) => row.key);
  const haplogroupDone = new Set((haploRows ?? []).map((row) => row.job_id));
  const admixtureDone = new Set((admixRows ?? []).map((row) => row.job_id));
  const summaryByJobId = new Map(
    (resultRows ?? []).map((row) => [row.job_id, row.summary as AnnotationSummary | null]),
  );

  for (const job of own) {
    const snapshot = summaryByJobId.get(job.id)?.annotation_config;
    const reasons: StalenessReason[] = [];

    // No snapshot at all (job predates this field) means no reliable
    // baseline to diff against — skip this signal rather than assume either
    // way.
    if (snapshot && enabledSources.some((key) => !snapshot[key])) {
      reasons.push("new_sources");
    }
    if (profile?.admixture_opt_in && !admixtureDone.has(job.id)) {
      reasons.push("admixture_pending");
    }
    if (profile?.haplogroup_opt_in && !haplogroupDone.has(job.id)) {
      reasons.push("haplogroup_pending");
    }

    result.set(job.id, { hasBenefit: reasons.length > 0, reasons });
  }
  return result;
}

/**
 * RLS returns the caller's own jobs, any jobs actively shared with them
 * (jobs_select_shared), AND — for an admin — every job in the entire system
 * (jobs_select_admin, meant only for the admin panel's separate member
 * overview) all mixed into one `select *`. This function must NOT infer "a
 * real share" from "a row I don't own that RLS let through" — for an admin
 * that inference is simply wrong and leaks every other user's job onto
 * their own personal dashboard mislabeled "Shared by X" with no real share
 * behind it (a real bug hit in production, 2026-09-05/07; job_shares was
 * confirmed empty both times). Real shares are determined explicitly below
 * via job_shares itself — the one genuine source of truth — and the
 * RLS-returned list is filtered down to owned-or-actually-shared before
 * anything is returned to the client.
 */
export const listJobs = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("jobs")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const allVisible = data ?? [];

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: activeShares } = await supabaseAdmin
      .from("job_shares")
      .select("job_id, owner_id")
      .eq("shared_with_user_id", userId)
      .eq("status", "active");
    const ownerIdByJobId = new Map((activeShares ?? []).map((s) => [s.job_id, s.owner_id]));

    const jobs = allVisible.filter((job) => job.user_id === userId || ownerIdByJobId.has(job.id));

    const ownerIds = Array.from(new Set(ownerIdByJobId.values()));
    const { data: owners } =
      ownerIds.length > 0
        ? await supabaseAdmin.from("profiles").select("user_id, email").in("user_id", ownerIds)
        : { data: [] as { user_id: string; email: string | null }[] };
    const emailByOwnerId = new Map((owners ?? []).map((o) => [o.user_id, o.email]));

    const staleness = await computeStaleness(supabase, userId, jobs);

    return jobs.map((job) => {
      const ownerId = ownerIdByJobId.get(job.id);
      return {
        ...job,
        isOwner: job.user_id === userId,
        sharedByEmail:
          job.user_id === userId ? null : ownerId ? (emailByOwnerId.get(ownerId) ?? null) : null,
        staleness: staleness.get(job.id) ?? null,
      };
    });
  });

export const getJobWithResult = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ jobId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: job, error } = await supabase
      .from("jobs")
      .select("*")
      .eq("id", data.jobId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!job) return { job: null, result: null };
    const { data: result, error: resultError } = await supabase
      .from("results")
      .select("*")
      .eq("job_id", data.jobId)
      .maybeSingle();
    if (resultError) throw new Error(resultError.message);

    const isOwner = job.user_id === userId;
    let share: { canDownload: boolean; ownerEmail: string | null } | null = null;
    if (!isOwner) {
      // The `job` row above came back from RLS, which for an admin also
      // returns every job system-wide (jobs_select_admin, for the admin
      // panel only) — not proof of a real share. Verify one explicitly
      // before exposing anything; no real job_shares row means this isn't
      // actually this caller's to view, full stop (same class of bug as
      // listJobs leaking admin-visible jobs as fake "shared" entries,
      // 2026-09-05/07).
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: shareRow } = await supabaseAdmin
        .from("job_shares")
        .select("can_download")
        .eq("job_id", data.jobId)
        .eq("shared_with_user_id", userId)
        .eq("status", "active")
        .maybeSingle();
      if (!shareRow) return { job: null, result: null };

      const { data: ownerProfile } = await supabaseAdmin
        .from("profiles")
        .select("email")
        .eq("user_id", job.user_id)
        .maybeSingle();
      share = { canDownload: shareRow.can_download, ownerEmail: ownerProfile?.email ?? null };
    }

    const staleness = isOwner
      ? ((await computeStaleness(supabase, userId, [job])).get(job.id) ?? null)
      : null;

    return { job, result, isOwner, share, staleness };
  });

export const deleteJob = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ jobId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { deleteOwnJob } = await import("@/lib/jobs.server");
    return deleteOwnJob(supabase, userId, data.jobId);
  });

export const deleteAllData = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: jobs } = await supabase
      .from("jobs")
      .select("id, storage_path")
      .eq("user_id", userId);
    const paths = (jobs ?? []).map((job) => job.storage_path).filter(Boolean);
    const { deleteUploadObjects, deleteJobResults } = await import("@/lib/storage.server");
    if (paths.length > 0) await deleteUploadObjects(paths);
    await deleteJobResults((jobs ?? []).map((job) => job.id as string));
    const { error } = await supabase.from("jobs").delete().eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true, deleted: paths.length };
  });

const searchParamsSchema = z.object({
  jobId: z.string().uuid(),
  term: z.string().max(500),
  scope: z.enum(["any", "gene", "rsid", "disease", "drug"]),
  includeCommon: z.boolean(),
  preset: z.enum(["pathogenic", "carrier", "pharmacogenomic"]).nullable(),
  impact: z.string().max(20).nullable(),
  maxFrequency: z.number().nullable(),
  minAlphaMissense: z.number().nullable(),
  genes: z.array(z.string().max(50)).max(50),
  zygosity: z.enum(["any", "reference", "heterozygous", "homozygous", "compound"]),
  clinicalSignificance: z
    .enum(["pathogenic", "benign", "uncertain", "conflicting", "risk_factor", "drug_response"])
    .nullable(),
});

/**
 * Runs the search natively on this server (jump01), over the same SSH
 * tunnel to MinIO storage.server.ts already uses — see search-query.server.ts
 * for why this replaced an earlier browser-side DuckDB-Wasm design.
 *
 * For a targeted rsID search that comes back with zero rows, also answers what
 * "not found" means for THIS report (an optional `absence` field on the
 * response): a well-covered WGS absence usually means the reference allele, an
 * array absence means the chip didn't test it, etc. — the wording and the case
 * split live in search-absence.ts. This is best-effort and never surfaces an
 * error to the user (see the handler).
 *
 * SECURITY (fixed 2026-10-01, found while building task V's multi-report
 * search): the comment this replaced claimed RLS alone (jobs_select_own /
 * jobs_select_shared) was enough to guarantee the caller is the owner or has
 * an active share. That is false for an admin caller — jobs_select_admin
 * ("is_admin()") also lets RLS return ANY job's row, for the admin panel's
 * member overview, which is a different thing from "this admin is allowed to
 * search this report's genetic data." Without the explicit check below, an
 * admin passing an arbitrary jobId (not owned, not shared) got `status`
 * back from the `.select()`, and this function then happily ran the real
 * search against that report's data in MinIO — the exact same class of bug
 * already found and fixed once in listJobs() (job_shares confirmed empty,
 * 2026-09-05/07) and once in genome.coach's family_facts.py (f754b3a). Same
 * fix here: job_shares (a service-role read of share metadata only — never
 * of report data) is the one genuine source of truth for "shared with me";
 * RLS is not.
 */
export const runSearch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => searchParamsSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { jobId, ...params } = data;
    const { data: job, error } = await supabase
      .from("jobs")
      .select("status, user_id")
      .eq("id", jobId)
      .maybeSingle();
    if (error || !job || job.status !== "ready") {
      throw new Error("Not found");
    }
    if (job.user_id !== userId) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: share } = await supabaseAdmin
        .from("job_shares")
        .select("job_id")
        .eq("job_id", jobId)
        .eq("shared_with_user_id", userId)
        .eq("status", "active")
        .maybeSingle();
      if (!share) {
        throw new Error("Not found");
      }
    }

    const { runServerSearch, lookupRsidForJob, parseSummary, resolveRsidSearch } =
      await import("@/lib/search-query.server");

    // A targeted rsID search goes through the ONE shared decision
    // (resolveRsidSearch, search-query.server.ts — also the logic behind POST
    // /api/lookup-rsids and, later, the genome.coach chat): the UI's FULL
    // params produce the rows shown; only when those are EMPTY is the clean
    // lookup consulted to attach an absence — or to learn the variant exists
    // but the user's filters excluded it (no absence then). results.summary is
    // read lazily (getMeta) and only on that empty path. A missing summary or
    // a lookup failure is logged and drops to the plain no-match result (the
    // generic text). Never a 500. Every other search kind is unchanged.
    if (classifyTerm(params.term) === "rsid") {
      return await resolveRsidSearch(
        jobId,
        params,
        async () => {
          try {
            const { data: resultRow, error: summaryError } = await supabase
              .from("results")
              .select("summary")
              .eq("job_id", jobId)
              .maybeSingle();
            if (summaryError) {
              console.error("[search] absence: could not read results.summary", summaryError);
              return null;
            }
            return parseSummary(resultRow?.summary ?? null);
          } catch (err) {
            console.error("[search] absence: could not read results.summary", err);
            return null;
          }
        },
        { runServerSearch, lookupRsidForJob },
      );
    }

    const { rows, total, approximate } = await runServerSearch(jobId, params);
    return { rows, total, approximate };
  });

// ---- "Search across all my reports" ---------------------------------------

/**
 * The search criteria for a multi-report search — the EXISTING SearchParams
 * (search-types.ts) minus the single-report `jobId`, since a multi-search
 * runs over the caller's whole ready set instead of one pinned job.
 */
const multiSearchParamsSchema = z.object({
  term: z.string().max(500),
  scope: z.enum(["any", "gene", "rsid", "disease", "drug"]),
  includeCommon: z.boolean(),
  preset: z.enum(["pathogenic", "carrier", "pharmacogenomic"]).nullable(),
  impact: z.string().max(20).nullable(),
  maxFrequency: z.number().nullable(),
  minAlphaMissense: z.number().nullable(),
  genes: z.array(z.string().max(50)).max(50),
  zygosity: z.enum(["any", "reference", "heterozygous", "homozygous", "compound"]),
  clinicalSignificance: z
    .enum(["pathogenic", "benign", "uncertain", "conflicting", "risk_factor", "drug_response"])
    .nullable(),
});

/**
 * The caller's ACTIVE shares, from job_shares — the one genuine source of
 * truth listJobs uses to tell a real share apart from an admin's system-wide
 * RLS visibility (see listJobs' comment and the 2026-09-05/07 production bug).
 * A service-role read of share METADATA only — never report data. If it fails,
 * an empty set: the caller's own reports stay searchable (safe, no leak), only
 * the shared ones are dropped for this call.
 */
async function readSharedJobIds(userId: string): Promise<Set<string>> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("job_shares")
      .select("job_id")
      .eq("shared_with_user_id", userId)
      .eq("status", "active");
    return new Set((data ?? []).map((row) => row.job_id as string));
  } catch (err) {
    console.error(
      "[multi-search] could not read job_shares; restricting to the caller's own reports",
      err,
    );
    return new Set();
  }
}

/**
 * The caller's ready reports, scoped the SAME way listJobs is — the RLS select
 * alone is NOT enough for an admin (RLS's jobs_select_admin also returns every
 * system job), so the own/shared filter (selectCandidateJobs) is what actually
 * scopes this. One RLS select (most-recent-first, ready only) + one job_shares
 * read. Shared by listMyReadyReports (the toggle's N) and runMultiSearch (the
 * search) so they can't drift on which reports are in play.
 */
async function loadCandidateIngredients(
  supabase: SupabaseClient<Database>,
  userId: string,
): Promise<{ allVisible: ReadyJobMeta[]; sharedJobIds: Set<string> }> {
  const [{ data, error }, sharedJobIds] = await Promise.all([
    supabase
      .from("jobs")
      .select("id, label, filename, user_id")
      .eq("status", "ready")
      .order("created_at", { ascending: false }),
    readSharedJobIds(userId),
  ]);
  if (error) throw new Error(error.message);
  return { allVisible: (data ?? []) as ReadyJobMeta[], sharedJobIds };
}

/**
 * The candidate reports a multi-search would run over (own + shared, ready,
 * most-recent-first, capped at 12) — so the "All my reports (N)" toggle can
 * show N before any search. Cheap: one RLS select + one job_shares read, no
 * DuckDB/Parquet. Returns the CandidateReports shape (search-types.ts).
 */
export const listMyReadyReports = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CandidateReports> => {
    const { supabase, userId } = context;
    const { allVisible, sharedJobIds } = await loadCandidateIngredients(supabase, userId);
    return selectCandidateJobs(allVisible, userId, sharedJobIds);
  });

/**
 * Search across ALL of the caller's ready reports at once (the "all my
 * reports" scope). Composes the EXISTING per-report search — runServerSearch
 * for the rows, and the shared lookupRsidForJob for a targeted-rsID absence —
 * rather than a new engine, so it can't drift from the single-report path or
 * POST /api/lookup-rsids.
 *
 * Candidate set: the caller's own + shared ready reports (loadCandidate
 * Ingredients — same scoping as listJobs, NOT the raw RLS list), intersected
 * with any pinned jobIds (a client can't probe reports it isn't cleared for),
 * capped at 12, searched at most 4 at a time (runMultiSearchCore's pool). A
 * per-report failure comes back as that report's error chip, never a failed
 * call. See multi-search.ts for the pool/cap/absence details.
 */
export const runMultiSearch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        params: multiSearchParamsSchema,
        jobIds: z.array(z.string().uuid()).max(32).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { runServerSearch, lookupRsidForJob, parseSummary } =
      await import("@/lib/search-query.server");

    const { allVisible, sharedJobIds } = await loadCandidateIngredients(supabase, userId);

    // The absence path (a targeted-rsID term that came back empty) reads the
    // report's own summary — the same lazy getMeta runSearch uses. RLS-scoped
    // (the caller's token), so we only ever read a summary for a report the
    // candidate list above already cleared for this caller.
    const getMeta = (jobId: string): Promise<ReportMeta | null> =>
      (async () => {
        try {
          const { data: resultRow, error: summaryError } = await supabase
            .from("results")
            .select("summary")
            .eq("job_id", jobId)
            .maybeSingle();
          if (summaryError) {
            console.error("[multi-search] absence: could not read results.summary", summaryError);
            return null;
          }
          return parseSummary(resultRow?.summary ?? null);
        } catch (err) {
          console.error("[multi-search] absence: could not read results.summary", err);
          return null;
        }
      })();

    return await runMultiSearchCore({
      params: data.params,
      jobIds: data.jobIds,
      callerUserId: userId,
      allVisible,
      sharedJobIds,
      runServerSearch,
      lookupRsidForJob,
      getMeta,
      onReportError: (jobId, err) => console.error(`[multi-search] report ${jobId} failed`, err),
    });
  });
